// 플러그인 호스트 — 부팅(해소·검사·순서·setup)과 실행(단계 훅·격리) (ADR-048 D2~D5)
import type { ComponentType } from 'react';

import type { Locale } from '../../ports/i18n';
import type { PluginConfigLayer } from '../../ports/plugin-config';
import type { SlotKey } from '../../ports/slots';
import type { StageKey, StagePayloads } from '../../ports/stages';
import { CircuitBreaker, DEFAULT_HOOK_TIMEOUT_MS, type BreakerOptions } from './breaker';
import type { AnyPlugin, MutatingHook, ObserverHook, PluginContext } from './contract';
import { resolvePluginConfig } from './resolve';

/** 부팅을 멈추는 오류 — 메시지에 플러그인 이름이 들어간다 (prd/103 FR-020) */
export class PluginBootError extends Error {
  constructor(
    readonly plugin: string,
    message: string,
  ) {
    super(`[plugin:${plugin}] ${message}`);
    this.name = 'PluginBootError';
  }
}

export type PluginErrorPhase = 'setup' | 'render' | 'mutate' | 'observe' | 'dispose';

export type PluginErrorReporter = (plugin: string, phase: PluginErrorPhase, error: unknown) => void;

export interface RouteEntry {
  readonly plugin: string;
  readonly path: string;
  readonly component: ComponentType;
}

export interface SlotEntry {
  /** 렌더 key — 한 플러그인이 같은 슬롯에 여러 위젯을 기여해도 겹치지 않는다 */
  readonly id: string;
  readonly plugin: string;
  readonly component: ComponentType;
  readonly priority: number;
}

interface MutatorEntry {
  readonly plugin: string;
  readonly priority: number;
  readonly order: number;
  readonly hook: MutatingHook<StageKey>;
}

interface ObserverEntry {
  readonly plugin: string;
  readonly hook: ObserverHook<StageKey>;
}

export interface BootOptions {
  /** 기본값 다음에 덮을 층 — [MVP 앱 설정, 원격 설정] 순 (좁은 쪽이 뒤) */
  readonly layers?: readonly PluginConfigLayer[];
  readonly hookTimeoutMs?: number;
  readonly breaker?: BreakerOptions;
  readonly onError?: PluginErrorReporter;
}

export interface PluginHost {
  /** 실제로 켜진(setup 성공) 플러그인 이름 — 부팅 순서 */
  readonly active: readonly string[];
  /** 꺼졌거나 setup 실패로 빠진 플러그인과 사유 */
  readonly skipped: ReadonlyMap<string, string>;
  readonly routes: readonly RouteEntry[];
  slots(key: SlotKey): readonly SlotEntry[];
  message(locale: Locale, key: string): string | undefined;
  /** 단계 실행 — 변경 훅을 priority 순으로 돌린 결과를 돌려주고, 관찰 훅은 기다리지 않는다 */
  runStage<S extends StageKey>(stage: S, payload: StagePayloads[S]): Promise<StagePayloads[S]>;
  /** 진행 중인 관찰 훅이 모두 끝날 때까지 (테스트·종료용) */
  settled(): Promise<void>;
  reportError: PluginErrorReporter;
  dispose(): void;
}

const DEFAULT_PRIORITY = 100;

/**
 * 훅에 넘길 사본 — 원본 payload 를 훅이 고쳐도 다른 훅·호출자에게 새지 않게 한다.
 * structuredClone 이 못 다루는 값(함수·Response 등)이 있으면 바깥 두 층만 얕게 복사한다 (그 안쪽은 공유된다).
 */
function copyPayload<T>(value: T): T {
  try {
    return structuredClone(value);
  } catch {
    const outer = { ...(value as object) } as Record<string, unknown>;
    for (const [k, v] of Object.entries(outer)) {
      if (v && typeof v === 'object' && !ArrayBuffer.isView(v)) outer[k] = Array.isArray(v) ? [...v] : { ...v };
    }
    return outer as T;
  }
}

function deepFreeze<T>(value: T): T {
  const freeze = (v: unknown) => {
    if (v && typeof v === 'object' && !Object.isFrozen(v) && !ArrayBuffer.isView(v)) {
      Object.freeze(v);
      Object.values(v).forEach(freeze);
    }
  };
  freeze(value);
  return value;
}

/** 변경 훅 결과가 같은 단계의 payload 모양인지 — 아니면 실패로 보고 입력을 유지한다 */
function isPayload(value: unknown, like: { action: string }): value is StagePayloads[StageKey] {
  return !!value && typeof value === 'object' && (value as { action?: unknown }).action === like.action;
}

const RESERVED_ROUTES = new Set(['/', '*']);

/** '/hello/' · 'hello' · '/hello' 를 같은 '/hello' 로 본다. 루트·와일드카드는 코어 소유라 거절 */
export function normalizeRoutePath(path: string): string | null {
  const trimmed = `/${path.trim().replace(/^\/+|\/+$/g, '')}`;
  if (RESERVED_ROUTES.has(trimmed) || trimmed.includes('*')) return null;
  return trimmed;
}

function withTimeout<T>(work: () => T | Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`훅이 ${ms}ms 안에 끝나지 않았다`)), ms);
    Promise.resolve()
      .then(work)
      .then(
        value => {
          clearTimeout(timer);
          resolve(value);
        },
        error => {
          clearTimeout(timer);
          reject(error);
        },
      );
  });
}

/** dependsOn 이 먼저 오도록 정렬 (같은 깊이는 이름순). 순환이면 부팅 중단 */
function orderByDependencies(plugins: readonly AnyPlugin[]): AnyPlugin[] {
  const byName = new Map(plugins.map(p => [p.name, p]));
  const ordered: AnyPlugin[] = [];
  const state = new Map<string, 'visiting' | 'done'>();
  const visit = (p: AnyPlugin, path: string[]) => {
    const s = state.get(p.name);
    if (s === 'done') return;
    if (s === 'visiting') throw new PluginBootError(p.name, `dependsOn 순환: ${[...path, p.name].join(' → ')}`);
    state.set(p.name, 'visiting');
    for (const dep of [...(p.dependsOn ?? [])].sort()) {
      const target = byName.get(dep);
      if (target) visit(target, [...path, p.name]);
    }
    state.set(p.name, 'done');
    ordered.push(p);
  };
  [...plugins].sort((a, b) => a.name.localeCompare(b.name)).forEach(p => visit(p, []));
  return ordered;
}

export function bootPlugins(registry: readonly AnyPlugin[], options: BootOptions = {}): PluginHost {
  const layers = options.layers ?? [];
  const hookTimeoutMs = options.hookTimeoutMs ?? DEFAULT_HOOK_TIMEOUT_MS;
  const reportError: PluginErrorReporter = options.onError ?? (() => {});

  // ① 이름 중복
  const seen = new Set<string>();
  for (const p of registry) {
    if (seen.has(p.name)) throw new PluginBootError(p.name, '같은 이름의 플러그인이 둘 등록됐다');
    seen.add(p.name);
  }

  // ② 설정 해소 + params 파싱 — 꺼진 플러그인은 여기서 빠지고 4축 어디에도 등록되지 않는다 (AC-022)
  const skipped = new Map<string, string>();
  const enabled: { plugin: AnyPlugin; params: unknown }[] = [];
  for (const plugin of registry) {
    const config = resolvePluginConfig(plugin.name, plugin.defaults, layers);
    if (!config.enabled) {
      skipped.set(plugin.name, 'enabled:false');
      continue;
    }
    let params: unknown = config.params;
    if (plugin.params) {
      const parsed = plugin.params.safeParse(config.params);
      if (!parsed.success) throw new PluginBootError(plugin.name, `params 파싱 실패 — ${parsed.error.message}`);
      params = parsed.data;
    }
    enabled.push({ plugin, params });
  }

  // ③ dependsOn·conflictsWith — 켜진 집합 기준으로 검사, 어긋나면 부팅 중단 (AC-019)
  const enabledNames = new Set(enabled.map(e => e.plugin.name));
  for (const { plugin } of enabled) {
    for (const dep of plugin.dependsOn ?? []) {
      if (!enabledNames.has(dep)) throw new PluginBootError(plugin.name, `dependsOn 미충족 — '${dep}' 가 켜져 있지 않다`);
    }
    for (const other of plugin.conflictsWith ?? []) {
      if (enabledNames.has(other)) throw new PluginBootError(plugin.name, `conflictsWith 동시 활성 — '${other}' 와 같이 켤 수 없다`);
    }
  }

  // ④ setup — 의존 순서대로. 실패한 플러그인의 기여는 버리고, 그 플러그인에 기대는 플러그인도 뺀다
  const paramsByName = new Map(enabled.map(e => [e.plugin.name, e.params]));
  const routes: RouteEntry[] = [];
  const slots = new Map<SlotKey, SlotEntry[]>();
  const mutators = new Map<StageKey, MutatorEntry[]>();
  const observers = new Map<StageKey, ObserverEntry[]>();
  const messages = new Map<Locale, Map<string, string>>();
  const active: string[] = [];
  let order = 0;

  for (const plugin of orderByDependencies(enabled.map(e => e.plugin))) {
    const missing = (plugin.dependsOn ?? []).find(dep => !active.includes(dep));
    if (missing) {
      skipped.set(plugin.name, `의존 플러그인 '${missing}' 의 setup 실패`);
      continue;
    }
    const staged: (() => void)[] = [];
    const stagedRoutes: RouteEntry[] = [];
    let closed = false;
    // setup 이 끝난 뒤(비동기 setup 의 await 이후 등) 들어온 기여는 받지 않는다 — 조용히 사라지지 않게 알린다
    const guard = (what: string) => {
      if (closed) reportError(plugin.name, 'setup', new Error(`setup 이 끝난 뒤 ctx.${what} 호출 — 무시한다 (setup 은 동기여야 한다)`));
      return closed;
    };
    const ctx: PluginContext = {
      name: plugin.name,
      route(path, component) {
        if (guard('route')) return;
        const normalized = normalizeRoutePath(path);
        if (!normalized) throw new PluginBootError(plugin.name, `라우트 '${path}' 는 코어 소유(루트·와일드카드)라 기여할 수 없다`);
        const clash = routes.find(r => r.path === normalized) ?? stagedRoutes.find(r => r.path === normalized);
        if (clash) throw new PluginBootError(plugin.name, `라우트 '${normalized}' 가 플러그인 '${clash.plugin}' 와 겹친다`);
        const entry = { plugin: plugin.name, path: normalized, component };
        stagedRoutes.push(entry);
        staged.push(() => routes.push(entry));
      },
      slot(key, component, opts) {
        if (guard('slot')) return;
        staged.push(() => {
          const list = slots.get(key) ?? [];
          list.push({ id: `${plugin.name}:${list.length}`, plugin: plugin.name, component, priority: opts?.priority ?? DEFAULT_PRIORITY });
          slots.set(key, list);
        });
      },
      mutate(stage, hook, opts) {
        if (guard('mutate')) return;
        staged.push(() => {
          const list = mutators.get(stage) ?? [];
          list.push({ plugin: plugin.name, priority: opts?.priority ?? DEFAULT_PRIORITY, order: order++, hook: hook as unknown as MutatingHook<StageKey> }); // 단계별 타입은 저장 시 지우고 runStage<S> 에서 되살린다
          mutators.set(stage, list);
        });
      },
      observe(stage, hook) {
        if (guard('observe')) return;
        staged.push(() => {
          const list = observers.get(stage) ?? [];
          list.push({ plugin: plugin.name, hook: hook as unknown as ObserverHook<StageKey> });
          observers.set(stage, list);
        });
      },
      messages(locale, dict) {
        if (guard('messages')) return;
        staged.push(() => {
          const table = messages.get(locale) ?? new Map<string, string>();
          for (const [k, v] of Object.entries(dict)) table.set(`${plugin.name}.${k}`, v);
          messages.set(locale, table);
        });
      },
    };
    try {
      const returned: unknown = plugin.setup(ctx, paramsByName.get(plugin.name));
      if (returned && typeof (returned as { then?: unknown }).then === 'function') {
        throw new Error('setup 이 Promise 를 돌려줬다 — setup 은 동기여야 한다 (비동기 준비는 훅·위젯 안에서)');
      }
    } catch (error) {
      if (error instanceof PluginBootError) throw error; // 선언 오류(라우트 충돌 등)는 부팅 중단
      reportError(plugin.name, 'setup', error);
      skipped.set(plugin.name, 'setup 실패');
      continue;
    } finally {
      closed = true;
    }
    staged.forEach(commit => commit()); // 검사는 기여 시점에 끝났다 — 여기서는 던지지 않는다
    active.push(plugin.name);
  }

  for (const list of slots.values()) list.sort((a, b) => a.priority - b.priority);
  for (const list of mutators.values()) list.sort((a, b) => a.priority - b.priority || a.order - b.order);

  // ⑤ 실행 — 변경 훅은 플러그인별 서킷 브레이커 + 타임아웃, 관찰 훅은 타임아웃만 (whif-engine safeExec)
  const breakers = new Map(active.map(name => [name, new CircuitBreaker(options.breaker)]));
  const pending = new Set<Promise<void>>();

  async function runStage<S extends StageKey>(stage: S, payload: StagePayloads[S]): Promise<StagePayloads[S]> {
    let current = payload;
    for (const entry of mutators.get(stage) ?? []) {
      const breaker = breakers.get(entry.plugin);
      const ticket = breaker?.acquire() ?? null;
      if (breaker === undefined || ticket === null) continue;
      try {
        // 사본을 넘긴다 — 훅이 입력을 고친 뒤 던지거나 타임아웃 뒤 늦게 고쳐도 current 는 그대로다
        const input = copyPayload(current);
        const output = await withTimeout(() => entry.hook(input), hookTimeoutMs);
        if (!isPayload(output, current)) throw new Error(`변경 훅이 ${stage} payload 가 아닌 값을 돌려줬다`);
        current = output as StagePayloads[S];
        breaker.success(ticket);
      } catch (error) {
        // 타임아웃이면 이 훅을 건너뛰고 앞 결과로 진행한다 (fail-open — whif-engine 과 같다. gate 로 막는 훅도 마찬가지)
        breaker.failure(ticket);
        reportError(entry.plugin, 'mutate', error);
      }
    }
    let snapshot: StagePayloads[S] | null = null;
    try {
      snapshot = deepFreeze(structuredClone(current));
    } catch (error) {
      // 관찰 훅은 얼린 사본만 받는다. 복제할 수 없는 값(함수 등)이 실려 있으면 관찰 훅을 건너뛰고 원인을 남긴다
      for (const entry of observers.get(stage) ?? []) {
        reportError(entry.plugin, 'observe', new Error(`${stage} payload 를 복제할 수 없어 관찰 훅을 건너뛴다 — ${String(error)}`));
      }
    }
    for (const entry of snapshot === null ? [] : (observers.get(stage) ?? [])) {
      const frozen = snapshot as StagePayloads[S];
      const run = withTimeout(() => entry.hook(frozen), hookTimeoutMs)
        .then(
          () => undefined,
          error => reportError(entry.plugin, 'observe', error),
        )
        .finally(() => pending.delete(run));
      pending.add(run);
    }
    return current;
  }

  return {
    active,
    skipped,
    routes,
    slots: key => slots.get(key) ?? [],
    message: (locale, key) => messages.get(locale)?.get(key),
    runStage,
    async settled() {
      while (pending.size > 0) await Promise.all(pending);
    },
    reportError,
    dispose() {
      for (const name of [...active].reverse()) {
        const plugin = registry.find(p => p.name === name);
        try {
          plugin?.dispose?.();
        } catch (error) {
          reportError(name, 'dispose', error);
        }
      }
    },
  };
}
